import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const readParam = (searchParams, key, defaultValue, parse) => {
  const raw = searchParams.get(key);
  if (parse) return parse(raw, defaultValue);
  return raw === null ? defaultValue : raw;
};

const formatParam = (value, defaultValue, format) => {
  if (Object.is(value, defaultValue)) return null;
  if (format) return format(value);
  return String(value);
};

export const intParam = (min, max) => (raw, fallback) => {
  if (raw === null || raw === '') return fallback;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < min || value > max) return fallback;
  return value;
};

export function readUrlFilters(spec) {
  const searchParams = new URLSearchParams(window.location.search);
  const values = {};
  Object.entries(spec).forEach(([key, config]) => {
    values[key] = readParam(searchParams, key, config.defaultValue, config.parse);
  });
  return values;
}

export function useUrlFilters(entries) {
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshot = JSON.stringify(entries.map((entry) => entry.value));
  const lastSnapshot = useRef(snapshot);
  const mounted = useRef(false);

  useEffect(() => {
    entries.forEach((entry) => {
      const raw = searchParams.get(entry.key);
      if (!mounted.current && raw === null) return;
      const next = readParam(searchParams, entry.key, entry.defaultValue, entry.parse);
      entry.setValue((previous) => (Object.is(previous, next) ? previous : next));
    });
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (snapshot === lastSnapshot.current) return;
    lastSnapshot.current = snapshot;
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      let changed = false;
      entries.forEach((entry) => {
        const serialized = formatParam(entry.value, entry.defaultValue, entry.format);
        const current = next.get(entry.key);
        if (serialized === null) {
          if (current !== null) {
            next.delete(entry.key);
            changed = true;
          }
        } else if (current !== serialized) {
          next.set(entry.key, serialized);
          changed = true;
        }
      });
      return changed ? next : previous;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, setSearchParams]);
}
